import React from 'react';
import { Button } from '@/components/ui/Button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { AlertMessage } from '@/components/ui/AlertMessage';

interface CompanySettingsTabProps {
  customerCode?: string;
  companyName: string;
  companyAlias: string;
  contactName: string;
  contactEmail: string;
  contactPhone: string;
  hasCompanyInfoChanges: boolean;
  hasContactInfoChanges: boolean;
  hasAliasChanged: boolean;
  loading: boolean;
  onCompanyNameChange: (value: string) => void;
  onCompanyAliasChange: (value: string) => void;
  onContactNameChange: (value: string) => void;
  onContactEmailChange: (value: string) => void;
  onContactPhoneChange: (value: string) => void;
  onSave: () => void;
  onReset: () => void;
  onResetCompanyInfo?: () => void;
  onResetContactInfo?: () => void;
}

export const CompanySettingsTab: React.FC<CompanySettingsTabProps> = ({
  customerCode,
  companyName,
  companyAlias,
  contactName,
  contactEmail,
  contactPhone,
  hasCompanyInfoChanges,
  hasContactInfoChanges,
  hasAliasChanged,
  loading,
  onCompanyNameChange,
  onCompanyAliasChange,
  onContactNameChange,
  onContactEmailChange,
  onContactPhoneChange,
  onSave,
  onReset,
  onResetCompanyInfo,
  onResetContactInfo,
}) => {

  return (
    <div className="space-y-4">
      <Card>
        <CardHeader>
          <CardTitle>회사 정보</CardTitle>
          <CardDescription>
            회사 정보를 수정하면 관련 서비스 설정에 자동으로 반영됩니다.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="customerId">고객사 ID</Label>
              <Input
                id="customerId"
                value={customerCode || ''}
                disabled
                className="bg-gray-100"
              />
              <p className="text-xs text-gray-500">고객사 ID는 변경할 수 없습니다.</p>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="companyAlias">회사 별칭 (Alias)</Label>
              <Input
                id="companyAlias"
                value={companyAlias}
                onChange={(e) => onCompanyAliasChange(e.target.value)}
                placeholder="예: mycompany"
              />
              <p className="text-xs text-gray-500">
                영문, 숫자, 하이픈만 사용 가능합니다. 서비스 URL에 사용됩니다.
              </p>
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="companyName">회사명</Label>
            <Input
              id="companyName"
              value={companyName}
              onChange={(e) => onCompanyNameChange(e.target.value)}
              placeholder="회사명을 입력하세요"
            />
          </div>

          {hasCompanyInfoChanges && (
            <AlertMessage 
              type="warning" 
              message={hasAliasChanged 
                ? "💡 회사 정보가 변경되었습니다. 저장하면 Nginx 설정이 자동으로 업데이트됩니다." 
                : "💡 회사 정보가 변경되었습니다. 저장하면 변경사항이 반영됩니다."} 
              onClose={() => {}} 
            />
          )}

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={onResetCompanyInfo || onReset}
              disabled={!hasCompanyInfoChanges}
            >
              초기화
            </Button>
            <Button
              onClick={onSave}
              disabled={!hasCompanyInfoChanges || loading}
            >
              {loading ? '저장 중...' : '저장'}
            </Button>
          </div>
        </CardContent>
      </Card>

      <Card>
        <CardHeader>
          <CardTitle>담당자 정보</CardTitle>
          <CardDescription>
            회사 담당자 연락처 정보를 관리합니다. 이 정보는 고객사 관리에서도 확인할 수 있습니다.
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid gap-6 md:grid-cols-2">
            <div className="space-y-2">
              <Label htmlFor="contactName">담당자 이름</Label>
              <Input
                id="contactName"
                value={contactName}
                onChange={(e) => onContactNameChange(e.target.value)}
                placeholder="담당자 이름을 입력하세요"
              />
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="contactEmail">담당자 이메일</Label>
              <Input
                id="contactEmail"
                type="email"
                value={contactEmail}
                onChange={(e) => onContactEmailChange(e.target.value)}
                placeholder="담당자 이메일을 입력하세요"
              />
            </div>
          </div>

          <div className="space-y-2">
            <Label htmlFor="contactPhone">담당자 연락처</Label>
            <Input
              id="contactPhone"
              value={contactPhone}
              onChange={(e) => onContactPhoneChange(e.target.value)}
              placeholder="담당자 연락처를 입력하세요 (예: 010-1234-5678)"
            />
          </div>

          {hasContactInfoChanges && (
            <AlertMessage 
              type="info" 
              message="💡 담당자 정보가 변경되었습니다. 저장하면 변경사항이 반영됩니다." 
              onClose={() => {}} 
            />
          )}

          <div className="flex justify-end space-x-2">
            <Button
              variant="outline"
              onClick={onResetContactInfo || onReset}
              disabled={!hasContactInfoChanges}
            >
              초기화
            </Button>
            <Button
              onClick={onSave}
              disabled={!hasContactInfoChanges || loading}
            >
              {loading ? '저장 중...' : '저장'}
            </Button>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

